import moment from 'moment';
import { eDateFilterType } from '../providers/ModalsProvider/Modals.types.ts';

export function formatDateFilter(
  date: Date,
  format = 'YYYY-MM-DDTHH:mm:ss.SSSZ',
): string {
  return moment(date).format(format);
}

/**
 * True when a picker range should count as an active date filter.
 *
 * RET-1739: either end alone is enough. The filter-count badges used to test
 * only the end date, so a start-only range — which does filter results —
 * contributed nothing to the count: no badge, and the button kept its
 * inactive label. That made an emptied view unexplainable to the user and
 * to support. Both the Navbar panel and the Buscar modal share this
 * predicate so the two counters cannot drift apart again.
 */
export function isDateFilterActive(
  dateRange?: [Date | undefined, Date | undefined],
): boolean {
  return !!(dateRange?.[0] || dateRange?.[1]);
}

/**
 * Resolves a picker range to the concrete pair of dates a filter should use.
 *
 * RET-1739: a one-sided range means that single calendar day. This is the one
 * definition of that rule — the query builder, the realtime socket predicate
 * and the on-screen filter summary all read it here so a lead can never be
 * admitted live that a refetch would exclude, and the chip can never display a
 * range the query did not ask for.
 *
 * Returns `null` when neither end is set.
 */
export function resolveDateRange(
  dateRange?: [Date | undefined, Date | undefined],
): { from: Date; to: Date } | null {
  const startDate = dateRange?.[0];
  const endDate = dateRange?.[1];
  const first = startDate ?? endDate;
  const second = endDate ?? startDate;

  if (!first || !second) return null;

  // Order defensively. The picker cannot produce an inverted range, but a
  // rehydrated or programmatically restored filter can, and an inverted window
  // reaches the backend as a range that matches nothing.
  return first <= second
    ? { from: first, to: second }
    : { from: second, to: first };
}

/**
 * Canonical lead-filter query-param builder. Every lead-filtering surface
 * (Directory, Pipe board, AdvanceSearch/Buscar, BulkMessage) goes through
 * this one function so a given calendar day means the same window
 * everywhere, and so no caller can emit a half-open range.
 *
 * Two invariants, both from RET-1739:
 *
 * 1. **Never half-open.** A one-sided range — the user picked a start and no
 *    end, which is every single-day filter mid-selection — resolves to that
 *    single calendar day. leads-service declares `created_at.to` as
 *    `required_with:created_at` (`GetLeadsRequest.php`), so `from` without
 *    `to` is a guaranteed 422 that bff-web masks as an opaque 424. Emitting
 *    both boundaries is what makes a single-day filter work at all.
 * 2. **One day semantic.** Boundaries are CDMX days for both CREATED_AT and
 *    LAST_ACTION_AT, matching the Analytics page and the analytics-service
 *    `mw_analytics_leads` MV, which buckets by
 *    `DATE_TRUNC('day', created_at - INTERVAL '6 hours')`. Previously this
 *    helper emitted UTC days while the Directory emitted CDMX days, so the
 *    same "26 de julio" returned different sets depending on which UI you
 *    filtered from.
 */
export function buildDateFilterParams(
  dateRange: [Date | undefined, Date | undefined],
  dateFilterType: eDateFilterType = eDateFilterType.CREATED_AT,
) {
  // A one-sided range collapses to a single day rather than an open interval.
  const resolved = resolveDateRange(dateRange);

  if (!resolved) return {};

  const { from, to } = resolved;
  const isLastAction = dateFilterType === eDateFilterType.LAST_ACTION_AT;
  return {
    [isLastAction ? 'lastActionAtFrom' : 'createdAtFrom']:
      buildCdmxDayBoundary(from).start,
    [isLastAction ? 'lastActionAtTo' : 'createdAtTo']:
      buildCdmxDayBoundary(to).end,
  };
}

// CDMX is UTC-6 with no DST observation (since 2022). This offset is fixed
// across all Leadsales workspaces today; switch to a workspace-level
// timezone field if/when the product expands beyond Mexico.
export const CDMX_OFFSET_HOURS = 6;

/**
 * Returns UTC instants bounding a single CDMX calendar day for the given
 * picker date, in ISO-8601 form with `Z` suffix. The output is independent
 * of the browser timezone — the "day" is always read from the picker's
 * displayed date label and interpreted as a CDMX day.
 *
 * Example: picker shows May 7, 2026 →
 *   { start: '2026-05-07T06:00:00.000Z', end: '2026-05-08T05:59:59.999Z' }
 *
 * This is the canonical day-window for Directory and Analytics pages so
 * they align with the analytics-service `mw_analytics_leads` materialized
 * view, which buckets by CDMX day via
 * `DATE_TRUNC('day', created_at - INTERVAL '6 hours')`.
 *
 * Every lead-filter surface shares this window via
 * `buildDateFilterParams` (RET-1739 unified the day semantics).
 */
export function buildCdmxDayBoundary(date: Date): {
  start: string;
  end: string;
} {
  // Read the calendar day from the picker's local-TZ representation. In
  // production the picker returns a local-midnight Date for the user's
  // browser TZ; the YYYY-MM-DD label always matches what the user clicked
  // regardless of their browser TZ.
  const ymd = moment(date).format('YYYY-MM-DD');
  const startHour = String(CDMX_OFFSET_HOURS).padStart(2, '0');
  const endHour = String(CDMX_OFFSET_HOURS - 1).padStart(2, '0');
  const start = `${ymd}T${startHour}:00:00.000Z`;
  const nextDayYmd = moment(ymd).add(1, 'day').format('YYYY-MM-DD');
  const end = `${nextDayYmd}T${endHour}:59:59.999Z`;
  return { start, end };
}

/**
 * Composes a CDMX-day range from a start picker date and an end picker
 * date.
 */
export function buildCdmxRange(
  startDate: Date,
  endDate: Date,
): { start: string; end: string } {
  return {
    start: buildCdmxDayBoundary(startDate).start,
    end: buildCdmxDayBoundary(endDate).end,
  };
}

export function isSessionExpired(expiresAt: number) {
  const currentTimestamp = Math.floor(Date.now() / 1000);
  return currentTimestamp > expiresAt;
}

/**
 * App i18n-language key -> BCP-47 tag for `Intl`.
 *
 * The app uses `br` for Brazilian Portuguese, but `br` is **Breton** to `Intl`,
 * and bare `es` is peninsular Spanish (comma decimals, trailing symbol) — wrong
 * for every market this product sells to. Both must be mapped.
 *
 * Extracted here from `src/pages/WhatsappApi/Credits.functions.ts`, which asked
 * for exactly this in a TODO once it became the second copy. A third consumer
 * (the RET-1719 checkout notice) made it worth doing. `languageToLocale` in
 * `src/hooks/Intercom/Intercom.functions.ts` is the remaining copy — it keys off
 * the same map and is left alone here to keep this change to one concern.
 */
const APP_LOCALE_TO_INTL_TAG: Record<string, string> = {
  es: 'es-MX',
  en: 'en-US',
  br: 'pt-BR',
};

export const toIntlLocale = (appLocale: string): string => {
  const lang = (appLocale || '').toLowerCase();
  return (
    APP_LOCALE_TO_INTL_TAG[lang] ??
    APP_LOCALE_TO_INTL_TAG[lang.split('-')[0]] ??
    'es-MX'
  );
};
